/**
 * Runtime configuration loader for Ameen Insurance Assistant
 *
 * Loads configuration from window._env_ (set by config.js)
 * Falls back to process.env only if window._env_ is not available
 *
 * Supports both:
 * - REACT_APP_API_BASE_URL: .NET backend for direct business APIs (JWT auth)
 * - REACT_APP_BACKEND_URL: Python backend for orchestration (ephemeral tokens)
 */

interface RuntimeConfig {
  // Python backend for multi-agent orchestration
  REACT_APP_BACKEND_URL: string;
  // .NET backend for direct business APIs (existing)
  REACT_APP_API_BASE_URL: string;
  // Optional additional config
  REACT_APP_NOMINATIM_API_URL?: string;
}

// Extend Window interface for TypeScript
declare global {
  interface Window {
    _env_?: Partial<RuntimeConfig>;
  }
}

/**
 * Get configuration from window._env_ or fallback to process.env
 */
function getConfig(): RuntimeConfig {
  // Primary: Use window._env_ from config.js
  if (typeof window !== "undefined" && window._env_) {
    console.log("📝 [CONFIG] Loading from config.js (runtime)");
    return {
      REACT_APP_BACKEND_URL: window._env_.REACT_APP_BACKEND_URL || "",
      REACT_APP_API_BASE_URL: window._env_.REACT_APP_API_BASE_URL || "",
      REACT_APP_NOMINATIM_API_URL:
        window._env_.REACT_APP_NOMINATIM_API_URL || "",
    };
  }

  // Fallback: Use process.env (build-time .env)
  console.log("📝 [CONFIG] Loading from process.env (build-time)");
  return {
    REACT_APP_BACKEND_URL:
      process.env.REACT_APP_BACKEND_URL || "http://localhost:8000",
    REACT_APP_API_BASE_URL: process.env.REACT_APP_API_BASE_URL || "",
    REACT_APP_NOMINATIM_API_URL: process.env.REACT_APP_NOMINATIM_API_URL || "",
  };
}

// Cached config
let cachedConfig: {
  BACKEND_URL: string;
  API_BASE_URL: string;
  GEMINI_API_KEY?: string;
} | null = null;

/**
 * Get runtime configuration
 * Returns a normalized config object with commonly used keys
 */
export function getRuntimeConfig() {
  if (cachedConfig) {
    return cachedConfig;
  }

  const config = getConfig();
  const backendUrl = (config.REACT_APP_BACKEND_URL || "").trim().replace(/\/+$/, "");
  cachedConfig = {
    BACKEND_URL: backendUrl,
    API_BASE_URL: (config.REACT_APP_API_BASE_URL || "").trim(),
    GEMINI_API_KEY: process.env.REACT_APP_GEMINI_API_KEY || "",
  };

  // Log loaded configuration (without sensitive data)
  console.log("✅ [CONFIG] Configuration loaded:", {
    BACKEND_URL: cachedConfig.BACKEND_URL ? "✓ Set" : "✗ Missing",
    API_BASE_URL: cachedConfig.API_BASE_URL ? "✓ Set" : "✗ Missing",
  });

  return cachedConfig;
}

// Export the raw config object for backwards compatibility
export const config = getConfig();
